// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as failoverDomainActions from 'common/modules/failoverDomain/actions';
import { connect } from 'react-redux';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { Loader } from 'common/components';
import { WithErrorPages } from 'common/components/WithPageNotFound/WithErrorPages';
import PageHeader from 'admin/failoverDomain/page/components/PageHeader/PageHeader';
import SummaryCards from 'admin/failoverDomain/page/components/SummaryCards/SummaryCards';
import ComputeResourceTable from 'admin/failoverDomain/page/components/ComputeResourceTable/ComputeResourceTable';
import { PageContent } from 'admin/failoverDomain/page/containers/Styles';
import FailoverDomainCreateDialog from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/FailoverDomainCreateDialog';
import HighAvailabilityDisabledAlert from 'admin/highAvailability/components/HighAvailabilityDisabledAlert/HighAvailabilityDisabledAlert';
import { isHighAvailabilityCrDisabled } from 'common/modules/settings/selectors';

export type FailoverDomainPageProps =
    RouteComponentProps<{ id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const FailoverDomainPage: React.FC<FailoverDomainPageProps> = ({
    failoverDomain,
    computeResources,
    hasMoreComputeResources,
    isLoading,
    isHaGloballyDisabled,
    failoverDomainActions: {
        getFailoverDomain,
        getFailoverDomainComputeResources,
        loadFailoverDomainComputeResourcesOnScroll,
        excludeFromDomain,
        toggleActiveState,
        toggleCrIsLocked,
        toggleCrIsInMaintenance,
    },
    match: { params },
}) => {
    const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);

    const failoverDomainId = parseInt(params.id, 10);

    const onMount = () => Promise.all([
        getFailoverDomain(failoverDomainId),
        getFailoverDomainComputeResources(failoverDomainId),
    ]);

    const handleExclude = (crId: number) => excludeFromDomain(crId, failoverDomainId);

    const handleToggleActive = (isActive: boolean) => toggleActiveState(failoverDomainId, { is_active: isActive });

    const handleToggleIsLocked = (crId: number, isLocked: boolean) =>
        toggleCrIsLocked(crId, isLocked, failoverDomainId);

    const handleToggleIsInMaintenance = (crId: number, isInMaintenance: boolean) =>
        toggleCrIsInMaintenance(crId, isInMaintenance, failoverDomainId);

    const handleEditDialogClose = () => setIsEditDialogOpen(false);

    const handleEditDialogSaved = () => {
        setIsEditDialogOpen(false);
        getFailoverDomain(failoverDomainId);
        getFailoverDomainComputeResources(failoverDomainId);
    };

    return (
        <WithErrorPages onMount={onMount} deps={[failoverDomainId]}>
            <Loader isLoading={isLoading}>
                <PageContent>
                    <PageHeader
                        failoverDomain={failoverDomain}
                        onToggleActive={handleToggleActive}
                        onSettingsClick={() => setIsEditDialogOpen(true)}
                        isToggling={failoverDomain.is_loading}
                    />
                    {isHaGloballyDisabled && <HighAvailabilityDisabledAlert />}
                    <SummaryCards
                        failoverDomain={failoverDomain}
                        computeResources={computeResources}
                    />
                    <ComputeResourceTable
                        items={computeResources}
                        onExclude={handleExclude}
                        updateIsLocked={handleToggleIsLocked}
                        updateIsInMaintenance={handleToggleIsInMaintenance}
                        loadMore={loadFailoverDomainComputeResourcesOnScroll}
                        hasMore={hasMoreComputeResources}
                    />
                </PageContent>
                {isEditDialogOpen && (
                    <FailoverDomainCreateDialog
                        editItem={failoverDomain}
                        onClose={handleEditDialogClose}
                        onSaved={handleEditDialogSaved}
                    />
                )}
            </Loader>
        </WithErrorPages>
    );
};

const mapStateToProps = (state: RootState) => ({
    failoverDomain: state.failoverDomain.item,
    computeResources: state.failoverDomain.computeResources.data,
    hasMoreComputeResources: !!state.failoverDomain.computeResources.links.next,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_ITEM),
    isHaGloballyDisabled: isHighAvailabilityCrDisabled(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    failoverDomainActions: bindActionCreators(failoverDomainActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FailoverDomainPage));
